import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import useAiJob from "@/hooks/useAiJob";
import {
    Question,
    Sparkle,
    ArrowRight,
    CheckCircle,
    Copy,
    Lightning,
    ChatCircleText,
    Buildings,
    Lightbulb,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const FOCUS_OPTIONS = [
    { key: "mixed", label: "Balanced mix", copy: "Behavioral + technical + situational" },
    { key: "behavioral", label: "Behavioral", copy: "STAR-format storytelling" },
    { key: "technical", label: "Technical", copy: "Role-specific deep dives" },
    { key: "system-design", label: "System Design", copy: "Architecture + trade-offs" },
];

const CATEGORY_STYLE = {
    behavioral: "bg-splash/20 text-ink border-splash/30",
    technical: "bg-cobalt/10 text-cobalt border-cobalt/30",
    situational: "bg-emerald-100 text-emerald-800 border-emerald-300",
    "system-design": "bg-purple-100 text-purple-800 border-purple-300",
};

export default function InterviewQuestionsPage() {
    const [role, setRole] = useState("");
    const [years, setYears] = useState(3);
    const [focus, setFocus] = useState("mixed");
    const [result, setResult] = useState(null);
    const [expanded, setExpanded] = useState({});
    const { data, error, pending, run } = useAiJob();
    const loading = pending;

    // Bridge the hook state into the existing rendering variable.
    useEffect(() => {
        if (data && (data.questions || data.role)) {
            setResult(data);
            setExpanded({});
            setTimeout(() => {
                document.getElementById("iq-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        }
    }, [data]);
    useEffect(() => {
        if (error) toast.error(error);
    }, [error]);

    const submit = async (e) => {
        e?.preventDefault();
        if (!role.trim()) {
            toast.error("Enter your role first");
            return;
        }
        run({
            url: "/interview/questions",
            body: { role: role.trim(), years: Number(years) || 0, focus },
        });
    };

    const copyOne = (q) => {
        const text = `Q: ${q.q}\n\nWhy asked: ${q.why_asked}\n\nSTAR answer template:\n${q.star_answer_template}\n\nFollow-ups:\n${(q.follow_ups || []).map((f) => `- ${f}`).join("\n")}`;
        navigator.clipboard.writeText(text).then(
            () => toast.success("Copied to clipboard"),
            () => toast.error("Copy failed"),
        );
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="interview-questions-page">
            <PublicHeader currentPath="/interview-questions" />

            {/* Hero + form */}
            <section className="relative bg-ink text-white overflow-hidden">
                <div className="absolute -right-20 -top-20 w-96 h-96 bg-splash/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -left-20 bottom-0 w-72 h-72 bg-cobalt/10 rounded-full blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-14 sm:pt-16 sm:pb-20">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.25em] text-splash mb-4 inline-flex items-center gap-2">
                        <ChatCircleText size={12} weight="fill" /> Free interview prep
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-4">
                        Practice the <br className="sm:hidden" />
                        questions you&apos;ll <br className="sm:hidden" />
                        <span className="text-splash">actually be asked.</span>
                    </h1>
                    <p className="text-base sm:text-lg text-white/70 max-w-xl mb-8 leading-relaxed">
                        8 tailored questions with STAR-format answer templates and follow-ups.
                        Generated by NaukriDaddy AI — trained on hiring-panel transcripts.
                    </p>

                    <form
                        onSubmit={submit}
                        data-testid="iq-form"
                        className="bg-white text-ink p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl shadow-2xl"
                    >
                        <div className="sm:col-span-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                Your role
                            </label>
                            <input
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                placeholder="e.g. Senior Backend Engineer, Product Manager"
                                data-testid="iq-input-role"
                                className="w-full border border-[#E5E5E5] px-3 py-3 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                                Years exp
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="40"
                                step="0.5"
                                value={years}
                                onChange={(e) => setYears(e.target.value)}
                                data-testid="iq-input-years"
                                className="w-full border border-[#E5E5E5] px-3 py-3 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-2">
                                Focus
                            </label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {FOCUS_OPTIONS.map((f) => (
                                    <button
                                        type="button"
                                        key={f.key}
                                        onClick={() => setFocus(f.key)}
                                        data-testid={`iq-focus-${f.key}`}
                                        className={`border px-3 py-2.5 text-left transition-colors ${
                                            focus === f.key
                                                ? "border-ink bg-ink text-white"
                                                : "border-[#E5E5E5] hover:border-ink"
                                        }`}
                                    >
                                        <p className="font-heading text-sm font-bold tracking-tight">{f.label}</p>
                                        <p className={`text-[10px] ${focus === f.key ? "text-white/60" : "text-[#525252]"} mt-0.5`}>
                                            {f.copy}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="sm:col-span-3">
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="iq-submit"
                                className="btn-gradient w-full sm:w-auto px-6 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {loading ? "Generating…" : (<><Sparkle size={14} weight="fill" /> Generate my 8 questions</>)}
                            </button>
                        </div>
                    </form>

                    <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-10 max-w-2xl">
                        {[
                            ["Questions / run", "8"],
                            ["Follow-ups per Q", "2"],
                            ["Powered by", "NaukriDaddy AI"],
                        ].map(([label, value]) => (
                            <div key={label}>
                                <p className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">
                                    {value}
                                </p>
                                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-white/50 mt-1">
                                    {label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Result */}
            {result && (
                <section id="iq-result" data-testid="iq-result" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                                Your interview kit
                            </p>
                            <h2 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                                {result.role} · {result.years} yr · {FOCUS_OPTIONS.find((f) => f.key === result.focus)?.label}
                            </h2>
                        </div>
                        <button
                            onClick={submit}
                            disabled={loading}
                            data-testid="iq-regenerate"
                            className="border border-ink hover:bg-ink hover:text-white text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors disabled:opacity-60"
                        >
                            <Lightning size={12} weight="bold" /> Regenerate
                        </button>
                    </div>

                    <div className="space-y-3">
                        {result.questions.map((q, idx) => {
                            const isOpen = !!expanded[idx];
                            const catStyle = CATEGORY_STYLE[q.category] || CATEGORY_STYLE.behavioral;
                            return (
                                <div
                                    key={idx}
                                    data-testid={`iq-question-${idx}`}
                                    className="bg-white border border-[#E5E5E5] p-5 sm:p-6"
                                >
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <span className="font-heading text-2xl font-black tracking-tighter text-cobalt shrink-0">
                                                {String(idx + 1).padStart(2, "0")}
                                            </span>
                                            <div className="min-w-0">
                                                <p className="font-heading text-base sm:text-lg font-bold tracking-tight leading-snug">
                                                    {q.q}
                                                </p>
                                                <span
                                                    className={`inline-flex items-center gap-1 mt-2 text-[10px] font-black uppercase tracking-wider border px-2 py-0.5 ${catStyle}`}
                                                >
                                                    <Question size={10} weight="fill" /> {q.category}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => copyOne(q)}
                                            data-testid={`iq-copy-${idx}`}
                                            className="text-[#525252] hover:text-ink shrink-0"
                                            title="Copy this question + answer template"
                                        >
                                            <Copy size={16} weight="bold" />
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => setExpanded((s) => ({ ...s, [idx]: !s[idx] }))}
                                        data-testid={`iq-toggle-${idx}`}
                                        className="text-[11px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark mt-2"
                                    >
                                        {isOpen ? "Hide answer template ↑" : "Show STAR answer template ↓"}
                                    </button>
                                    {isOpen && (
                                        <div className="mt-4 pt-4 border-t border-[#F5F5F5] space-y-3">
                                            {q.why_asked && (
                                                <div>
                                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-1 flex items-center gap-1.5">
                                                        <Lightbulb size={11} weight="fill" /> Why they&apos;re asking
                                                    </p>
                                                    <p className="text-sm text-[#525252] leading-relaxed">{q.why_asked}</p>
                                                </div>
                                            )}
                                            {q.star_answer_template && (
                                                <div>
                                                    <p className="text-[10px] font-black uppercase tracking-wider text-splash mb-1 flex items-center gap-1.5">
                                                        <Sparkle size={11} weight="fill" /> STAR template
                                                    </p>
                                                    <p className="text-sm text-ink leading-relaxed whitespace-pre-line">
                                                        {q.star_answer_template}
                                                    </p>
                                                </div>
                                            )}
                                            {q.follow_ups && q.follow_ups.length > 0 && (
                                                <div>
                                                    <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-1 flex items-center gap-1.5">
                                                        <ArrowRight size={11} weight="bold" /> Likely follow-ups
                                                    </p>
                                                    <ul className="text-sm text-[#525252] space-y-1">
                                                        {q.follow_ups.map((f, i) => (
                                                            <li key={i} className="flex items-start gap-2">
                                                                <span className="text-cobalt">›</span> {f}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Next step CTA */}
                    <div className="mt-10 bg-ink text-white p-6 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2">
                                Next step
                            </p>
                            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                                Practiced the questions?
                                <br className="hidden sm:inline" /> Now polish the CV that got you here.
                            </h2>
                        </div>
                        <Link
                            to="/rate-my-cv"
                            data-testid="iq-cta-rate-cv"
                            className="btn-gradient px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                            <Sparkle size={14} weight="bold" /> Rate my CV — Free <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </section>
            )}

            {/* Why this works */}
            {!result && (
                <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-3">
                        Why this works
                    </p>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl mb-8">
                        Not generic interview lists. Actually tailored.
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                            { title: "Role + seniority-aware", copy: "A 2-yr SDE gets different questions than a 10-yr staff engineer. Same for PMs, designers, sales." },
                            { title: "Why-they're-asking notes", copy: "Every question explains what the interviewer is actually probing for — bias, culture-fit, depth, or resilience." },
                            { title: "Follow-ups included", copy: "The real interview isn't the first question — it's the two follow-ups that come after. We pre-warn you." },
                        ].map((c) => (
                            <div key={c.title} className="bg-white border border-[#E5E5E5] p-5">
                                <p className="font-heading text-lg font-bold tracking-tight mb-2">{c.title}</p>
                                <p className="text-sm text-[#525252] leading-relaxed">{c.copy}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
